import {
  queryOptions,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { eventKeys } from '../events/keys';
import { staffKeys } from '../staff/keys';

import {
  dispatchEvent,
  dispatchEvents,
  getBatch,
  getPublicReport,
  getMessageQueue,
  getReport,
  getSenderHealth,
  getStaffMessage,
  previewDispatch,
  reconnectSender,
  retryDelivery,
  sendEventNotice,
  sendQueueNow,
  sendReminder,
  sendReport,
} from './api';
import { dispatchKeys } from './keys';
import {
  type Batch,
  type EventNoticeInput,
  type MessageQueue,
  type ReconnectInput,
  type ReconnectResult,
  type WaitingReason,
} from './schemas';

import type { ApiError } from '../../api/errors';

/**
 * How often the Sending screen asks while a batch is going out.
 *
 * Messages to staff now go 20–60 s apart, so asking every second would be
 * dozens of requests per message. Every few seconds still shows each one land.
 */
export const BATCH_POLL_MS = 3000;

/**
 * How often it asks while the batch is HELD — overnight, behind the daily
 * limit, or while the number is down. Nothing moves for a while; checking
 * twice a minute is plenty, and it matters for a phone left on the screen.
 */
export const BATCH_HELD_POLL_MS = 30_000;

/** Held — overnight, behind the daily limit, the number down — asks slowly. */
function pollIntervalFor(reason: WaitingReason | null | undefined): number {
  return reason === 'quiet_hours' || reason === 'daily_limit' || reason === 'sender_down'
    ? BATCH_HELD_POLL_MS
    : BATCH_POLL_MS;
}

/** The poll interval for a batch in this state, or false to stop. */
function batchPollInterval(batch: Batch | undefined): number | false {
  if (batch?.finished === true) return false;
  return pollIntervalFor(batch?.pacing?.waitingReason);
}

/** The poll interval for the queue, or false once nothing waits. */
function queuePollInterval(queue: MessageQueue | undefined): number | false {
  if (!queue || queue.items.length === 0) return false;
  return pollIntervalFor(queue.waitingReason);
}

export function batchOptions(batchId: string) {
  return queryOptions({
    queryKey: dispatchKeys.batch(batchId),
    queryFn: () => getBatch(batchId),
  });
}

/**
 * Polls a batch until it finishes, then stops.
 *
 * `refetchInterval` returns false once `finished` is true rather than the
 * component unmounting the query: a finished batch is still ON SCREEN — the
 * coordinator is reading which ones failed — and it must stop costing a request
 * a second while they do. The server decides `finished`; deriving it here from
 * the counts would be a second opinion on when a send is over.
 */
export function useBatchQuery(batchId: string | undefined) {
  return useQuery({
    ...batchOptions(batchId ?? ''),
    enabled: batchId !== undefined && batchId !== '',
    refetchInterval: (query) => batchPollInterval(query.state.data),
    /*
     * Keep polling with the screen backgrounded.
     *
     * A send takes tens of seconds and the coordinator switches apps while it
     * runs. Without this the queue appears frozen at whatever it reached when
     * they left, which reads as a crash.
     */
    refetchIntervalInBackground: true,
  });
}

/**
 * Several batches watched as ONE send.
 *
 * The server caps a send at ten events, so a plan with more goes out as several
 * batches — but to the coordinator it is one send, and the Sending screen is one
 * list. Each batch polls on its own and stops when IT finishes, exactly like
 * `useBatchQuery`; the combined view is finished only once every batch is, and
 * that is still the server's word, not a count worked out here.
 */
export function useBatchesQuery(batchIds: readonly string[]) {
  return useQueries({
    queries: batchIds.map((batchId) => ({
      ...batchOptions(batchId),
      refetchInterval: (query: { state: { data?: Batch } }) => batchPollInterval(query.state.data),
      refetchIntervalInBackground: true,
    })),
    combine: (results) => {
      const batches = results.flatMap((result) => (result.data ? [result.data] : []));

      return {
        batches,
        deliveries: batches.flatMap((batch) => batch.deliveries),
        counts: batches.reduce(
          (sum, batch) => ({
            total: sum.total + batch.counts.total,
            queued: sum.queued + batch.counts.queued,
            sending: sum.sending + batch.counts.sending,
            sent: sum.sent + batch.counts.sent,
            failed: sum.failed + batch.counts.failed,
          }),
          { total: 0, queued: 0, sending: 0, sent: 0, failed: 0 },
        ),
        finished: results.length > 0 && results.every((result) => result.data?.finished === true),
        refetch: () => Promise.all(results.map((result) => result.refetch())),
      };
    },
  });
}

/**
 * Both mutations here invalidate the EVENT as well as the batch.
 *
 * Dispatching marks people notified, which changes the event's counts and can
 * change its badge. Forgetting that is how the Sending screen says "all sent"
 * while the event behind it still reads "not yet asked".
 */
function useBatchMutation<TVariables>(mutationFn: (variables: TVariables) => Promise<Batch>) {
  const queryClient = useQueryClient();

  return useMutation<Batch, ApiError, TVariables>({
    mutationFn,
    onSuccess: async (batch) => {
      queryClient.setQueryData(dispatchKeys.batch(batch.id), batch);
      await Promise.all([
        batch.eventId === null
          ? Promise.resolve()
          : queryClient.invalidateQueries({ queryKey: eventKeys.detail(batch.eventId) }),
        queryClient.invalidateQueries({ queryKey: eventKeys.lists() }),
      ]);
    },
  });
}

export function useDispatchEvent() {
  return useBatchMutation<string>(dispatchEvent);
}

/**
 * Dispatches several events at once.
 *
 * Invalidates `eventKeys.all` rather than one detail: the shared mutation path
 * only knows how to invalidate `batch.eventId`, which is NULL for a multi-event
 * batch — every selected event's detail would otherwise sit stale showing
 * nobody notified.
 */
export function useDispatchEvents(staffIds?: string[]) {
  const queryClient = useQueryClient();

  return useMutation<Batch, ApiError, string[]>({
    mutationFn: (eventIds) => dispatchEvents(eventIds, staffIds),
    onSuccess: async (batch) => {
      queryClient.setQueryData(dispatchKeys.batch(batch.id), batch);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: eventKeys.all }),
        // A person's own page lists what is still to send them.
        queryClient.invalidateQueries({ queryKey: staffKeys.details() }),
      ]);
    },
  });
}

/**
 * The messages for a set of events, for copying and sending by hand.
 *
 * A QUERY rather than a mutation, deliberately: re-opening the screen should
 * serve the same messages from cache rather than re-minting every link and
 * quietly killing the ones already pasted. Only a genuine refetch re-mints.
 */
export function useDispatchPreview(eventIds: string[], staffIds?: string[]) {
  return useQuery({
    queryKey: dispatchKeys.preview(eventIds, staffIds),
    queryFn: () => previewDispatch(eventIds, staffIds),
    enabled: eventIds.length > 0,
    // The links it mints stay valid until something re-mints them, so there is
    // nothing time-sensitive to chase.
    staleTime: Infinity,
  });
}

/**
 * The same preview, fetched ON DEMAND rather than by rendering.
 *
 * For the case where the messages are wanted at the moment of a tap — copying
 * one person's — and the screen had no reason to ask for them before. It writes
 * into the cache `useDispatchPreview` reads, so one fetch serves the tap and
 * every other row at once, and neither path re-mints what the other minted.
 */
export function useEnsureDispatchPreview() {
  const queryClient = useQueryClient();

  return (eventIds: string[], staffIds?: string[]) =>
    queryClient.fetchQuery({
      queryKey: dispatchKeys.preview(eventIds, staffIds),
      queryFn: () => previewDispatch(eventIds, staffIds),
      staleTime: Infinity,
    });
}

/** What the copy button gets for one person: their message, or why there is none. */
export type CopyableMessage =
  | { kind: 'message'; body: string }
  /** Opted out of messages: nothing is composed for them. */
  | { kind: 'opted_out' }
  /** Nothing to give — already asked, and their message's link has since been replaced. */
  | { kind: 'none' };

/**
 * One person's message on an event, fetched at the moment of a tap — for the
 * copy button on the event screen.
 *
 * The message ALREADY composed comes first: the one that reached them, or the
 * one still queued or failed. Only that stored body carries the reply link
 * they were given; a fresh one would mint another and kill it.
 *
 * Nobody messaged yet falls back to the event's PREVIEW — the same cached
 * entry the send screen reads, so both hand out the same link rather than each
 * minting over the other.
 */
export function useEnsureStaffMessage() {
  const queryClient = useQueryClient();
  const ensurePreview = useEnsureDispatchPreview();

  return async (eventId: string, staffId: string): Promise<CopyableMessage> => {
    const stored = await queryClient.fetchQuery({
      queryKey: dispatchKeys.message(eventId, staffId),
      queryFn: () => getStaffMessage(eventId, staffId),
      // Its status moves while the queue drains; always ask again.
      staleTime: 0,
    });
    if (stored.body !== null) return { kind: 'message', body: stored.body };

    const preview = await ensurePreview([eventId]);
    const body = preview.messages.find((message) => message.staffId === staffId)?.body;
    if (body !== undefined) return { kind: 'message', body };

    return preview.skippedOptedOut.includes(stored.staffName)
      ? { kind: 'opted_out' }
      : { kind: 'none' };
  };
}

export interface SendReminderVariables {
  eventId: string;
  staffId: string;
}

/**
 * "Send reminder" on the mark sheet. Through the shared batch path, so the
 * event re-reads and shows the reminder waiting to go.
 */
export function useSendReminder() {
  return useBatchMutation<SendReminderVariables>(({ eventId, staffId }) =>
    sendReminder(eventId, staffId),
  );
}

export interface SendEventNoticeVariables {
  eventId: string;
  input: EventNoticeInput;
}

/**
 * "Message staff" on an event: a template or free text to the people picked.
 * Through the shared batch path, so the Sending screen can poll it and the
 * event re-reads.
 */
export function useSendEventNotice() {
  return useBatchMutation<SendEventNoticeVariables>(({ eventId, input }) =>
    sendEventNotice(eventId, input),
  );
}

/**
 * One person's stored message on an event — the query form of what the copy
 * button fetches on a tap.
 *
 * The mark sheet reads it to know whether a reminder is possible at all:
 * `body: null` means no stored message still carries a working link (it was
 * sent by hand, or replaced), and a reminder has nothing to repeat.
 */
export function useStaffMessageQuery(eventId: string | undefined, staffId: string | undefined) {
  return useQuery({
    queryKey: dispatchKeys.message(eventId ?? '', staffId ?? ''),
    queryFn: () => getStaffMessage(eventId ?? '', staffId ?? ''),
    enabled: eventId !== undefined && eventId !== '' && staffId !== undefined && staffId !== '',
    // Its link can be replaced by any send; always ask again.
    staleTime: 0,
  });
}

export function useRetryDelivery() {
  return useBatchMutation<string>(retryDelivery);
}

export function useSendReport() {
  return useBatchMutation<string>(sendReport);
}

/**
 * The report text.
 *
 * Fetched rather than composed on the device, because it is the record of what
 * was agreed and two renderers would be two slightly different records. The old
 * mobile `reportText` did compose it locally; this replaces it.
 */
export function useReportQuery(eventId: string | undefined) {
  return useQuery({
    queryKey: dispatchKeys.report(eventId ?? ''),
    queryFn: () => getReport(eventId ?? ''),
    enabled: eventId !== undefined && eventId !== '',
  });
}

/**
 * The manager's public report, by link token.
 *
 * No retry on a 404 or 410: the link is unknown or finished, and asking again
 * only delays the page saying so. Anything else retries as normal.
 */
export function usePublicReportQuery(token: string | undefined) {
  return useQuery({
    queryKey: dispatchKeys.publicReport(token ?? ''),
    queryFn: () => getPublicReport(token ?? ''),
    enabled: token !== undefined && token !== '',
    retry: (failureCount, error: ApiError) =>
      error.status !== 404 && error.status !== 410 && failureCount < 3,
  });
}

/**
 * Whether the WhatsApp number can send, for Settings and the home banner.
 *
 * Re-read every minute while on screen: a disconnect or restriction should
 * show up without a pull-to-refresh, and the server remembers the answer for
 * a few seconds, so this costs the gateway nothing.
 */
export function useSenderHealthQuery() {
  return useQuery({
    queryKey: dispatchKeys.senderHealth(),
    queryFn: getSenderHealth,
    refetchInterval: 60_000,
  });
}

/**
 * The Waiting messages screen's queue. Polls while anything waits — every few
 * seconds while it sends, twice a minute while it is held — and stops once it
 * is empty.
 */
export function useMessageQueueQuery() {
  return useQuery({
    queryKey: dispatchKeys.messageQueue(),
    queryFn: getMessageQueue,
    refetchInterval: (query) => queuePollInterval(query.state.data),
  });
}

/**
 * One step of reconnecting WhatsApp. The sender's health is looked at again
 * after each step — that is where "connected" shows up.
 */
export function useReconnectSender() {
  const queryClient = useQueryClient();

  return useMutation<ReconnectResult, ApiError, ReconnectInput>({
    mutationFn: reconnectSender,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: dispatchKeys.senderHealth() });
    },
  });
}

/**
 * Send now. The answer IS the queue as sending starts, so it goes straight
 * into the cache; the sender row and any Sending screen are told to look again.
 */
export function useSendQueueNow() {
  const queryClient = useQueryClient();

  return useMutation<MessageQueue, ApiError, void>({
    mutationFn: sendQueueNow,
    onSuccess: async (queue) => {
      queryClient.setQueryData(dispatchKeys.messageQueue(), queue);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: dispatchKeys.senderHealth() }),
        queryClient.invalidateQueries({ queryKey: dispatchKeys.batches() }),
      ]);
    },
  });
}
